// @flow
import * as React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import { loadImage } from '../../loadImage.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import { ContentContext } from '../../JsxContent.js';

const ORIGIN = 'https://jsx-images.mathspace.co';

// The Tennessee RFP requires us to give students "technology"
// to use when doing their mathematics. In practice this means we are
// giving them a bunch of half-assed "calculator" tools they can use while
// working on questions. We need screenshots of the tools in action to
// guide the students in how to use them. This requires bitmap images.
// In general, it is very important that content authors can only use svgs
// in content. Therefore we do NOT want to allow normal Image components
// to support bitmaps. As a result, we have this very specialized component
// that is the only place that bitmaps can get into content. The hope is
// this will be locked down to enough to prevent abuse/contagion of bitmap
// images throughout the content.
export function INVTechnologyToolScreenshot(props: {|
  // The path to the image in the bucket. This is relative to
  // the technology-tool-screenshots "folder".
  path: string,
  // The description that screenreaders will use to describe the image to a user
  accessibilityDescription: string,
  // Dimensions are mandatory so that the layout can be computed
  // by the browser before any image data has been received.
  width: number,
  height: number,
  ...CompilerInjectedProps,
|}) {
  const { enablePhoneLayout } = React.useContext(ContentContext);
  const { width, height, path, accessibilityDescription } = props;
  const imageUrl = ORIGIN + '/technology-tool-screenshots' + path;

  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    INVTechnologyToolScreenshot,
  );

  React.useEffect(() => {
    if (failedValidation || !props._performValidation) return;

    // Async validation
    let isStaleEffect = false;
    validateIsPng(imageUrl, locations, addErrors, isStaleEffect);

    return () => {
      isStaleEffect = true;
    };
  });

  // validate provided dimensions
  React.useEffect(() => {
    if (failedValidation || !props._performValidation) return;
    if (width !== 596) {
      addErrors([
        {
          message: `The only valid \`width\` of an INVTechnologyToolScreenshot is 596px.`,
          ...locations.width,
        },
      ]);
      return;
    }
    let staleEffect = false;
    (async () => {
      try {
        const imageNode = await loadImage(imageUrl);
        if (staleEffect) return;
        const { naturalWidth, naturalHeight } = imageNode;
        if (naturalWidth < 2.5 * width) {
          addErrors([
            {
              message: `The natural width of the image at ${path} is too small. It should be at least 2.5x the width of an INVTechnologyToolScreenshot (596px).`,
              ...locations.INVTechnologyToolScreenshot,
            },
          ]);
          return;
        }
        if (naturalWidth / naturalHeight !== width / height) {
          addErrors([
            {
              message: [
                `The natural aspect ratio of the image at ${path} is distorted by the given \'width\' and \`height\`.`,
                `For a width of 569px, the height should be ${
                  (naturalHeight * width) / naturalWidth
                }px`,
              ].join('\n'),
              ...locations.INVTechnologyToolScreenshot,
            },
          ]);
          return;
        }
      } catch {
        if (staleEffect) return;
        addErrors([
          {
            message: `The image at ${path} could not be loaded.`,
            ...locations.path,
          },
        ]);
      }
    })();
    return () => {
      staleEffect = true;
    };
  });

  return (
    <img
      style={{ maxWidth: width }}
      src={imageUrl}
      alt={accessibilityDescription}
      width={enablePhoneLayout ? '100%' : width}
      height={enablePhoneLayout ? 'auto' : height}
    />
  );
}
INVTechnologyToolScreenshot.spec = {
  width: t.number,
  height: t.number,
  path: pathChecker,
  accessibilityDescription: t.string,
};

// Enforces that paths are strings which provide a versionId /blah?versionId=1234
function pathChecker(props, propName) {
  const value = props[propName];

  const failure = t.string(props, propName);
  if (failure !== null) return failure;
  // Refinement for Flow as it doesn't know t.string enforces a string type
  if (typeof value !== 'string') {
    throw Error('runtime typecheck invariant failure');
  }

  const url = new URL(`http://fake${value}`);
  const versionId = url.searchParams.get('versionId');
  if (versionId === null) {
    return {
      typeDescription: pathChecker.description,
      reason: 'must specify a versionId.',
    };
  }

  if (!/investigation-\d+.png/.test(value)) {
    return {
      typeDescription: pathChecker.description,
      reason:
        'INV documents can only contain <INVTechnologyToolScreenshot>s that source images identified with `investigation`',
    };
  }

  return null;
}
pathChecker.description = 'BucketPath';

async function validateIsPng(imageUrl, locations, addErrors, isStaleEffect) {
  const res = await fetch(imageUrl);
  const blob = await res.blob();
  if (isStaleEffect) return;
  if (blob.type !== 'image/png') {
    addErrors([
      {
        message: `You must use a .png for screenshots. The image type is ${blob.type}`,
        ...locations.INVTechnologyToolScreenshot,
      },
    ]);
  }
}
